import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Spinner } from '@gsa/afp-component-library';
import {
  AdditionalRequirements, AdditionalRequirementsAggregatedPriceEstimate,
  DetailGroupGroup,
  OneThird,
  UserContact,
} from '../../../../../components/Areq/AreqDetails/AreqDetails.jsx';
import {
  filterBuildableContracts, filterUnbuildableContracts,
  groupContractsByVendorName,
} from '../../../../areqMultipleVendors/areqMultipleVendorUtil.jsx';
import { concatStrings } from '../../../../../utilities/formatUtil.jsx';
import { useGetActiveContracts, useGetCalculatedPrice, useGetStandardCodes } from '../../../../../requests/leasing.jsx';
import { getSelectedPayload, TRANSACTION_TYPES } from '../../../../../utilities/CompareVehicleUtils.jsx';
import { LeasingSteps, RequisitionViewMode } from '../../../leasing-consts.js';
import SectionEditButton from '../../../../../components/ReviewComponents/SectionEditButton/SectionEditButton.jsx';
import useLeasingNavigation from '../../../../../hooks/useLeasingNavigation.jsx';

const getBuildableVendorNames = (activeContracts,contractCostBreakDown ) => {
  const buildableContracts = filterBuildableContracts({
    allActiveContracts: activeContracts,
    allCostBreakdown: undefined,
    contractCostBreakDowns: contractCostBreakDown,
  });

  if (!buildableContracts?.length === 0) {
    return [];
  }

  return Object.keys(groupContractsByVendorName(buildableContracts));
};
const getUnBuildableVendorNames = (activeContracts,contractCostBreakDown ) => {
  const unbuildableContracts = filterUnbuildableContracts({
    allActiveContracts: activeContracts,
    allCostBreakdown: undefined,
    contractCostBreakDowns: contractCostBreakDown,
  });

  if (!unbuildableContracts) {
    return [];
  }

  return Object.keys(groupContractsByVendorName(unbuildableContracts));
};

export const LeasingAreqDetails = () => {

  const createdByUser = useSelector((state) => state.leasingReducer.createdByUserInfo);
  const selectedStandardItem = useSelector((state) => state.leasingReducer.selectedStandardItem);
  const vehicleQuantity = useSelector((state) => state.leasingReducer.vehicleQuantity);
  const additionalRequirements = useSelector((state) => state.leasingReducer.additionalRequirements);
  const compareSelect = useSelector((state) => state.leasingReducer.compareSelect);
  const selectedOptions = useSelector((state) => state.leasingReducer.selectedOptions);
  const account = useSelector((state) => state.leasingReducer.account);
  const viewMode = useSelector((state) => state.leasingReducer.viewMode);
  const {calculatedPriceBreakdown, activeContracts} = compareSelect;
  const [getStandardCodes, {loading: isStandardCodesLoading}] = useGetStandardCodes();
  const [primary, setPrimary] = useState({});
  const [secondary, setSecondary] = useState({});
  const [buildableVendorNames, setBuildableVendorNames] = useState([]);
  const [unBuildableVendorNames, setUnBuildableVendorNames] = useState([]);
  const [getContracts, { loading: isActiveContractsLoading }] =
    useGetActiveContracts(0, 5);
  const [getCalculatedPrice, { loading: isCalculatePriceLoading }] =
    useGetCalculatedPrice();
  const { handleLeasingNavigation } = useLeasingNavigation();

  const editPage = (step) => {
    handleLeasingNavigation(step);
  };
  useMemo(() => {
    if(selectedStandardItem?.id) {
      if(!activeContracts?.length) {
        getContracts({
          variables: {
            standardItemId: +selectedStandardItem?.id,
            transactionType: TRANSACTION_TYPES.LEASING,
          },
        });
      }
      if(!calculatedPriceBreakdown?.length) {
        getCalculatedPrice({
          variables: {
            standardItemId: parseFloat(selectedStandardItem?.id),
            quantity: vehicleQuantity,
            selectedOptions: selectedOptions?.length > 0 ? getSelectedPayload(selectedOptions) : [],
            transactionType: TRANSACTION_TYPES.LEASING,
            fmcId: parseInt(account?.fmcId),
            standardItemNumber: selectedStandardItem?.standardItemNumber
          },
        });
      }
    }
    if (selectedStandardItem?.vehicleTypeCode?.parentCode) {
      getStandardCodes({
        variables: {
          filters: {
            operator: 'EQ',
            key: 'code',
            value: selectedStandardItem.vehicleTypeCode.parentCode,
          },
        },
      }).then(result => {
        if(result?.data?.rows?.length > 0){
          const standardCodePocs = result?.data?.rows.reduce(
            (list, code) => {
              return list.concat(code.standardsVehicleGroupPocs || []);
            },
            [],
          );
          setPrimary(standardCodePocs.find((c) => c.pocType === 'PocPrimaryEngineer') || {});
          setSecondary(standardCodePocs.find((c) => c.pocType === 'PocSecondaryEngineer') || {});
        }
      });
    }
  }, [selectedStandardItem]);

  useMemo(() => {
    console.log({activeContracts, calculatedPriceBreakdown});
    if(activeContracts?.length > 0 && calculatedPriceBreakdown){
      setBuildableVendorNames(getBuildableVendorNames(activeContracts,calculatedPriceBreakdown));
      setUnBuildableVendorNames(getUnBuildableVendorNames(activeContracts,calculatedPriceBreakdown));
    }
  }, [activeContracts,calculatedPriceBreakdown ]);
  if(isActiveContractsLoading || isCalculatePriceLoading || isStandardCodesLoading){
      return <Spinner />;

  }
   return(<>

    <div className="detail">
      <div className="title-section">
        <span>AREQ Requistion information</span>
        <SectionEditButton
          showButton={viewMode === RequisitionViewMode.EDIT}
          id="options-per-vehicle-edit"
          step={LeasingSteps.AREQ_DETAILS}
          editPage={editPage}
        />
      </div>
        <div role="tab" tabIndex="0">
          <OneThird>
            <DetailGroupGroup header="AREQ preparer">
              <UserContact
                name={concatStrings(
                  createdByUser?.firstName,
                  createdByUser?.lastName,
                )}
                email={createdByUser?.email}
                phoneNumber={createdByUser?.phoneNumber}
              />
            </DetailGroupGroup>
            <DetailGroupGroup header="Primary engineer">
              <UserContact
                name={primary.name}
                email={primary.email}
                phoneNumber={primary.phoneNumber}
              />
            </DetailGroupGroup>
            <DetailGroupGroup header="Secondary engineer contact">
              <UserContact
                name={secondary.name}
                email={secondary.email}
                phoneNumber={secondary.phoneNumber}
              />
            </DetailGroupGroup>
          </OneThird>
          <DetailGroupGroup>
            <AdditionalRequirements
              additionalRequirements={additionalRequirements}
            />
          </DetailGroupGroup>

          <DetailGroupGroup header="Aggregate price estimate of AREQ from Independent Government Cost Estimate (IGCE)">
            <AdditionalRequirementsAggregatedPriceEstimate
              additionalRequirements={additionalRequirements}
            />
          </DetailGroupGroup>
          <DetailGroupGroup header="Eligible vendors for AREQ">
            {buildableVendorNames.length
              ? buildableVendorNames.map((vendorName) => {
                return (
                  <div className="review-options-option-row">
                    {vendorName}
                  </div>
                );
              })
              : '--'}
          </DetailGroupGroup>

          <DetailGroupGroup header="Non-eligible vendors for this AREQ">
            {unBuildableVendorNames.length
              ? unBuildableVendorNames.map((vendorName) => {
                return (
                  <div className="review-options-option-row">
                    {vendorName}
                  </div>
                );
              })
              : '--'}
          </DetailGroupGroup>
        </div>
    </div>

  </>);
}